import React, { useContext, useState, useEffect, useRef } from "react";
import {
  Table,
  Row,
  Col,
  InputNumber,
  Button,
  Form,
  Input,
  Select,
  message,
} from "antd";
import { constructDisplayPrice, randomNumberGenerator } from "utils/general";
import {
  PlusOutlined,
  DeleteOutlined,
  DownOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { isArray } from "util";
import { patchAdviseeCashInflow } from "apis/advisee";
import { useParams, Prompt } from "react-router-dom";
import { useFetchConstant } from "../../../../../../../apis/constant";
import Locale from "react-intl-universal";

const EditableContext = React.createContext(null);

const EditableType = {
  number: "number",
  text: "text",
  selector: "selector",
};
const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  editableType,
  ...restProps
}) => {
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  const [editing, setEditing] = useState(false);
  const [incomeType, setIncomeType] = useState([]);

  const {
    isLoading: isIncomeTypesLoading,
    selection: incomeTypes,
  } = useFetchConstant("incomeTypes");

  useEffect(() => {
    if (editing && dataIndex !== "type") {
      inputRef.current.focus();
    }
  }, [editing]);

  useEffect(() => {
    setIncomeType(incomeTypes);
  }, []);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({
      [dataIndex]: record[dataIndex],
    });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({ ...record, ...values }, record.key);
    } catch (errInfo) {
      console.log("Save failed:", errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      editableType === EditableType.selector ? (
        <Form.Item
          style={{
            margin: 0,
          }}
          name={dataIndex}
          rules={[
            {
              required: true,
              message: `${title} is required.`,
            },
          ]}
        >
          <Select
            defaultOpen
            autoFocus
            onBlur={save}
            showSearch
            onSearch={(e) => {
              var customIncomeType = {
                code: "otherIncome",
                label: e,
              };
              var buffer = [...incomeType];
              buffer[buffer.length - 1] = customIncomeType;
              setIncomeType(buffer);
              handleSave({ ...record, label: e }, record.key);
            }}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {incomeType.map((value) => (
              <Select.Option key={value.code} value={value.code}>
                {value.code === "otherIncome"
                  ? `Others-${value.label}`
                  : value.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      ) : editableType === EditableType.number ? (
        <Form.Item
          style={{
            margin: 0,
          }}
          name={dataIndex}
          rules={[
            {
              required: true,
              message: `${title} is required.`,
            },
            {
              type: "number",
              message: "Invalid number format",
            },
          ]}
          initialValue={0}
        >
          <InputNumber
            min={0}
            precision={2}
            ref={inputRef}
            onPressEnter={save}
            onBlur={save}
            placeholder="100.00"
            style={{ width: "100%" }}
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) => value.replace(/(,*)/g, "")}
          />
        </Form.Item>
      ) : (
        <Form.Item
          style={{
            margin: 0,
          }}
          name={dataIndex}
          rules={[
            {
              required: false,
              message: `${title} is required.`,
            },
          ]}
          initialValue={""}
        >
          <Input
            ref={inputRef}
            onPressEnter={save}
            onBlur={save}
            placeholder="Remarks"
            style={{ width: "100%" }}
          />
        </Form.Item>
      )
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{
          paddingRight: 24,
        }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

const EditableTable = (props) => {
  const { id: adviseeId } = useParams();
  const [loading, setLoading] = useState(false);
  const [saveReminder, setSaveReminder] = useState(false);
  const [dataSource, setDataSource] = useState([
    // {
    //   key: "1",
    //   type: "Salary (Nett salary after deduct EPF)",
    //   self: 0,
    //   spouse: 0,
    //   total: 0
    // }
  ]);

  const {
    isLoading: isIncomeTypesLoading,
    selection: incomeTypes,
  } = useFetchConstant("incomeTypes");

  const [incomeType, setIncomeType] = useState([]);

  useEffect(() => {
    setIncomeType(incomeTypes);
  }, [incomeTypes]);

  const columnConfig = [
    {
      title: Locale.get("cashFlowSummary.cashInflowSummary.source"),
      dataIndex: "type",
      key: "type",
      editable: true,
      editableType: EditableType.selector,
      render: (value, record) => {
        const label = incomeType.find((v) => v.code === value);
        return (
          <div
            style={{
              padding: 8,
              border: "1px solid #e0e0e0",
              cursor: "pointer",
              borderRadius: 8,
              width: "auto",
            }}
          >
            <Row justify="space-between">
              <Col>
                <span>
                  {label ? label.label : "-"}
                  {record.label ? ` - ${record.label}` : ""}
                </span>
              </Col>
              <Col>
                <DownOutlined style={{ color: "#e0e0e0" }} />
              </Col>
            </Row>
          </div>
        );
      },
    },
    {
      title: Locale.get("cashFlowSummary.self"),
      dataIndex: "self",
      key: "self",
      editable: true,
      editableType: EditableType.number,
      render: (value) => (
        <div
          style={{
            padding: 8,
            border: "1px solid #e0e0e0",
            cursor: "pointer",
            borderRadius: 8,
            width: "auto",
          }}
        >
          <span>{value ? constructDisplayPrice(value, false) : "-"}</span>
        </div>
      ),
    },
    {
      title: Locale.get("cashFlowSummary.spouse"),
      dataIndex: "spouse",
      key: "spouse",
      editable: true,
      editableType: EditableType.number,
      render: (value) => (
        <div
          style={{
            padding: 8,
            border: "1px solid #e0e0e0",
            cursor: "pointer",
            borderRadius: 8,
            width: "auto",
          }}
        >
          <span>{value ? constructDisplayPrice(value, false) : "-"}</span>
        </div>
      ),
    },
    {
      title: Locale.get("cashFlowSummary.total"),
      render: (value, record) => {
        const selfAmt = Number(record.self) || 0;
        const spouseAmt = Number(record.spouse) || 0;
        const total = selfAmt + spouseAmt;
        return <span>{total ? constructDisplayPrice(total, false) : "-"}</span>;
      },
    },
    {
      title: Locale.get("cashFlowSummary.cashInflowSummary.remark"),
      dataIndex: "remarks",
      key: "remarks",
      width: "35%",
      editable: true,
      editableType: EditableType.text,
      render: (value) => (
        <div
          style={{
            padding: 8,
            border: "1px solid #e0e0e0",
            cursor: "pointer",
            borderRadius: 8,
            width: "auto",
          }}
        >
          <span>{value ? value : "-"}</span>
        </div>
      ),
    },
    {
      title: Locale.get("cashFlowSummary.cashInflowSummary.action"),
      align: "end",
      render: (value, record, index) => {
        return (
          <Button
            type="link"
            style={{ color: "red" }}
            disabled={loading}
            onClick={() => handleDelete(index)}
          >
            <DeleteOutlined />{" "}
            {Locale.get("cashFlowSummary.cashInflowSummary.delete")}
          </Button>
        );
      },
    },
  ];

  useEffect(() => {
    if (isArray(props.data) === true && props.data.length > 0) {
      const template = [...props.incomeTemplate];
      const filteredArray = template.filter((value) => {
        return props.data.findIndex((x) => x.type === value.type) < 0;
      });
      setEditIdentifierKey([...props.data, ...filteredArray]);
    } else {
      setEditIdentifierKey(props.incomeTemplate);
    }
  }, [props.data, props.incomeTemplate]);

  useEffect(() => {
    const triggerReminder = (ev) => {
      if (saveReminder) {
        ev.preventDefault();
        return (ev.returnValue = Locale.get(
          "cashFlowSummary.cashInflowSummary.beforeClose"
        ));
      }
    };
    if (saveReminder) {
      window.addEventListener("beforeunload", triggerReminder);
      window.addEventListener("unload", triggerReminder);
    } else {
      window.removeEventListener("beforeunload", triggerReminder);
      window.removeEventListener("unload", triggerReminder);
    }
    return () => {
      window.removeEventListener("beforeunload", triggerReminder);
      window.removeEventListener("unload", triggerReminder);
    };
  }, [saveReminder]);

  const handleSaveIncome = async () => {
    var payload = [...dataSource];
    // payload = payload.filter(value => {
    //   return value.self !== 0 || value.spouse !== 0;
    // });

    payload.forEach((income) => {
      income.amount = {
        self: income.self ? income.self : 0,
        spouse: income.spouse ? income.spouse : 0,
      };
    });

    setLoading(true);

    patchAdviseeCashInflow(adviseeId, {
      sources: payload,
    })
      .then((res) => {
        setLoading(false);
        setSaveReminder(false);
        message.success(
          Locale.get("cashFlowSummary.cashInflowSummary.successMsg")
        );
      })
      .catch((ex) => {
        setLoading(false);
        message.error(
          "Error in switching off the overridden setting. Please check your connection and try again."
        );
      });
  };

  const setEditIdentifierKey = (data) => {
    var buffer = [...data];
    buffer.forEach((x) => (x.key = randomNumberGenerator()));
    setDataSource(buffer);
  };

  const handleDelete = (dataIndex) => {
    var newData = dataSource.filter((item, index) => index !== dataIndex);
    setDataSource(newData);
    setSaveReminder(true);
    setSaveReminder(true);
  };

  const handleAdd = () => {
    const newData = {
      key: randomNumberGenerator(),
      type: "otherIncome",
      self: 0,
      spouse: 0,
      total: 0,
    };
    setDataSource([...dataSource, newData]);
    setSaveReminder(true);
  };

  const handleSave = (row) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, { ...item, ...row });
    setDataSource(newData);
    setSaveReminder(true);
  };

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };
  const columns = columnConfig.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        title: col.title,
        editable: col.editable,
        dataIndex: col.dataIndex,
        editableType: col.editableType,
        handleSave: handleSave,
      }),
    };
  });

  return (
    <div style={{ marginTop: 32 }}>
      <Row justify="end" style={{ marginBottom: 16 }}>
        <Col>
          {/* <Button
            type="primary"
            onClick={handleAdd}
            style={{
              marginRight: 8,
            }}
            disabled={loading}
          >
            <PlusOutlined /> Add a Row
          </Button> */}

          <Button
            type="primary"
            loading={loading}
            icon={<SaveOutlined />}
            onClick={handleSaveIncome}
          >
            {Locale.get("cashFlowSummary.cashInflowSummary.saveButton")}
          </Button>
        </Col>
      </Row>
      <Table
        rowKey="key"
        components={components}
        rowClassName={() => "editable-row"}
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        loading={props.loading}
        style={{
          marginBottom: 16,
        }}
      />
      <Button
        onClick={handleAdd}
        type="dashed"
        style={{
          marginTop: 16,
          width: "100%",
          borderColor: "#d7a301",
          color: "#d7a301",
        }}
      >
        <PlusOutlined />{" "}
        {Locale.get("cashFlowSummary.cashInflowSummary.addRowButton")}
      </Button>
      <Prompt
        when={saveReminder}
        message={Locale.get("cashFlowSummary.cashInflowSummary.promptMessage")}
      />
    </div>
  );
};

export default EditableTable;
